import { useMemo } from 'react'

import { REACT_QUERY_KEYS, bridgeRequest } from 'common'
import { TEndpoint, TReportType } from 'contexts/app-context/types'
import { useAppContext } from 'contexts/app-context/hooks/useAppContext'
import { logBridgeError, sendSelectContent } from 'common/helpers'
import { Routes } from 'common/constants'
import { REPORT_DICTIONARY } from 'common/types/br/Response.types'

import { useMutation, useQueryClient } from '@tanstack/react-query'

export const useRequestReport = (reportType: TReportType) => {
  const { privacyCenterContent, userSelectedEntity, isUSBranch } = useAppContext()

  const queryClient = useQueryClient()

  const requestReportEndpoint: TEndpoint | undefined = useMemo(() => {
    const endpoint = privacyCenterContent?.endpoints?.reports?.requestReport

    if (!endpoint) return undefined

    return {
      ...endpoint,
      headers: { ...endpoint.headers, 'x-inter-tipo-solicitacao': reportType },
    }
  }, [privacyCenterContent?.endpoints?.reports?.requestReport, reportType])

  const {
    mutateAsync: requestReport,
    isPending: isRequestedReportLoading,
    isError: isRequestedReportError,
    isSuccess: isRequestedReportSuccess,
    data: requestedReport,
    reset,
  } = useMutation({
    mutationFn: async () => {
      if (!requestReportEndpoint) throw new Error('No request report endpoint available')

      try {
        await sendSelectContent({
          flow: 'request_report',
          step: 'your_personal_data',
          screen_name: Routes.YOUR_PERSONAL_DATA,
          element_name: 'requestReport',
          element_type: 'button',
          parent_name: `requestReport - ${reportType}`,
          scenario: JSON.stringify({
            screen: Routes.YOUR_PERSONAL_DATA,
            product: userSelectedEntity,
            url: requestReportEndpoint.url,
            reportType,
            ref_type: 'button',
            content_action: 'touch',
          }),
        })
      } catch (error: unknown) {
        logBridgeError('send request report tic', error)
      }

      return bridgeRequest({
        endpoint: {
          ...requestReportEndpoint,
          headers: {
            ...requestReportEndpoint.headers,
            'x-inter-tipo-solicitacao': REPORT_DICTIONARY[reportType],
          },
        },
        returnAllResponse: false,
        isUSBranch,
      })
    },
    onSuccess: () =>
      Promise.all([
        queryClient.refetchQueries({
          queryKey: [REACT_QUERY_KEYS.REPORTS_IN_ANALYSIS],
        }),
        queryClient.refetchQueries({
          queryKey: [REACT_QUERY_KEYS.REPORTS_HISTORY],
        }),
        queryClient.refetchQueries({
          queryKey: [REACT_QUERY_KEYS.REPORT_STATUS, reportType],
        }),
      ]),
  })

  return {
    requestReport,
    isRequestedReportLoading,
    isRequestedReportError,
    isRequestedReportSuccess,
    requestedReport,
    reset,
  }
}
